import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, CalendarCheck } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { useSettings } from "@/contexts/SettingsContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { CATEGORY_KEYS } from "@/i18n";

export default function CategoryPage() {
  const { category } = useParams();
  const { t } = useLang();
  const { img } = useSettings();
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    api.get("/products", { params: { category } }).then((r) => setProducts(r.data)).catch(() => setProducts([])).finally(() => setLoaded(true));
    window.scrollTo(0, 0);
  }, [category]);

  const isCat = CATEGORY_KEYS.includes(category);
  const title = isCat ? t(`cat_${category}`) : t("nav_accessories");
  const desc = isCat ? t(`cat_${category}_desc`) : "";
  const hero = img(isCat ? `cat_${category}` : "cat_brautmode");

  return (
    <div className="relative min-h-screen">
      <div className="vl-noise" />
      <Header />
      <section className="relative h-[52vh] min-h-[360px] flex items-end pt-20 overflow-hidden" data-testid="category-hero">
        <img src={hero} alt={title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 w-full pb-12">
          <div className="text-xs tracking-[0.35em] uppercase text-[var(--vl-gold)] mb-3">Veloura · Stuttgart</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light img-caption">{title}</h1>
          {desc && <p className="text-base img-caption-sub mt-3 max-w-xl">{desc}</p>}
        </div>
      </section>

      <section className="relative z-10 py-16 px-6 sm:px-8">
        <div className="max-w-7xl mx-auto">
          {loaded && products.length === 0 && (
            <div className="vl-card rounded-2xl p-10 text-center max-w-xl mx-auto" data-testid="category-empty">
              <CalendarCheck size={34} className="text-[var(--vl-gold)] mx-auto mb-4" />
              <p className="text-[var(--vl-stone)]">{t("no_products_yet")}</p>
              <Link to="/termin" className="vl-btn-gold rounded-full px-7 py-3.5 text-sm tracking-widest uppercase font-medium inline-flex items-center gap-2 mt-6">{t("nav_appointment")} <ArrowRight size={15} /></Link>
            </div>
          )}
          {products.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="category-grid">
              {products.map((p) => (
                <div key={p.id} className="vl-card rounded-2xl overflow-hidden group" data-testid={`product-${p.id}`}>
                  <a href={p.image} target="_blank" rel="noopener noreferrer" className="block h-80 overflow-hidden" title={t("view_image")} data-testid={`product-image-${p.id}`}>
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </a>
                  <div className="p-5">
                    {p.subcategory && <div className="text-[10px] tracking-widest uppercase text-[var(--vl-gold)] mb-1">{p.subcategory}</div>}
                    <h3 className="font-serif-display text-xl text-[var(--vl-ivory)]">{p.title}</h3>
                    {p.description && <p className="text-sm text-[var(--vl-stone)] mt-1">{p.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {products.length > 0 && (
            <div className="text-center mt-12"><Link to="/termin" className="vl-btn-gold rounded-full px-8 py-4 text-sm tracking-widest uppercase font-medium inline-flex items-center gap-2">{t("nav_appointment")} <ArrowRight size={16} /></Link></div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
